import { Dialog as DialogPrimitive } from '@base-ui-components/react/dialog';
import { css, StyleDeclarationValue, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { XIcon } from '../../components/svgs/icons/XIcon';
import { DropdownPortalContainerContext } from '../DropdownPortalContainerContext';
import { fadeIn, fadeInZoomIn, fadeOut, fadeOutZoomOut } from '../../styles/animations';
import { borderColor, white } from '../../styles/colors';
import { shadowLg } from '../../styles/shadows';
import { CssSelectors } from '../../styles/styles';
import { THEME } from '../Theme/theme';

/**
 * Dialog root component.
 * @param dismissOnOutsideClick - When true (default), clicking outside the dialog closes it.
 *                                When false, clicking outside does NOT close the dialog.
 */
function Dialog({
	dismissOnOutsideClick = true,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Root> & {
	dismissOnOutsideClick?: boolean;
}) {
	return <DialogPrimitive.Root data-slot='dialog' disablePointerDismissal={!dismissOnOutsideClick} {...props} />;
}
function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
	return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />;
}
function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
	return <DialogPrimitive.Portal data-slot='dialog-portal' {...props} />;
}
function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
	return <DialogPrimitive.Close data-slot='dialog-close' {...props} />;
}
function DialogBackdrop({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof DialogPrimitive.Backdrop>>) {
	return (
		<DialogPrimitive.Backdrop data-slot='dialog-overlay' className={css(s.dialogBackdrop, ...styles)} {...props} />
	);
}
function DialogPopup({
	styles = [],
	children,
	showCloseButton = true,
	overlay = true,
	variant = 'default',
	keepMounted = false,
	dialogCloseStyles = [],
	...props
}: AphroditeStyled<
	React.ComponentProps<typeof DialogPrimitive.Popup> & {
		keepMounted?: boolean;
		variant?: 'default';
		showCloseButton?: boolean;
		overlay?: boolean;
		dialogCloseStyles?: StyleDeclarationValue[];
	}
>) {
	const popupRef = React.useRef<HTMLDivElement>(null);
	return (
		<DialogPortal keepMounted={keepMounted} className={css(!overlay && s.hidePortal)}>
			{overlay ? <DialogBackdrop /> : null}
			<DialogPrimitive.Popup
				ref={popupRef}
				data-slot='dialog-content'
				className={css(s.dialogPopup, variant === 'default' && s.dialogContentDefault, ...styles)}
				{...props}
			>
				<DropdownPortalContainerContext.Provider value={popupRef}>
					{children}
					{showCloseButton && (
						<DialogClose className={css(s.dialogClose, ...dialogCloseStyles)}>
							<XIcon className={css(s.closeIcon)} />
							<span className={css(s.srOnly)}>Close</span>
						</DialogClose>
					)}
				</DropdownPortalContainerContext.Provider>
			</DialogPrimitive.Popup>
		</DialogPortal>
	);
}
const DialogHeader = ({ styles = [], ...props }: AphroditeStyled<React.HTMLAttributes<HTMLDivElement>>) => (
	<div data-slot='dialog-header' className={css(s.dialogHeader, ...styles)} {...props} />
);
const DialogFooter = ({ styles = [], ...props }: AphroditeStyled<React.HTMLAttributes<HTMLDivElement>>) => (
	<div data-slot='dialog-footer' className={css(s.dialogFooter, ...styles)} {...props} />
);
function DialogTitle({ styles = [], ...props }: AphroditeStyled<React.ComponentProps<typeof DialogPrimitive.Title>>) {
	return <DialogPrimitive.Title data-slot='dialog-title' className={css(s.dialogTitle, ...styles)} {...props} />;
}
const DialogBody = ({ styles = [], ...props }: AphroditeStyled<React.HTMLAttributes<HTMLDivElement>>) => (
	<div data-slot='dialog-body' className={css(s.dialogBody, ...styles)} {...props} />
);
function DialogDescription({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof DialogPrimitive.Description>>) {
	return (
		<DialogPrimitive.Description
			data-slot='dialog-description'
			className={css(s.dialogDescription, ...styles)}
			{...props}
		/>
	);
}
export {
	Dialog,
	DialogBackdrop,
	DialogBody,
	DialogClose,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
};

const s = StyleSheet.create({
	hidePortal: {
		...CssSelectors.allChildren(
			{
				display: 'none',
			},
			':first-child'
		),
	},
	dialogBackdrop: {
		position: 'fixed',
		display: 'none',
		inset: 0,
		zIndex: 50,
		backgroundColor: 'rgba(0, 0, 0, 0.3)',
		backdropFilter: 'blur(4px)',
		animationDuration: '150ms',
		animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
		animationFillMode: 'forwards',
		':is([data-open])': {
			display: 'block',
			animationName: fadeIn,
		},
		':is([data-closed])': {
			animationName: fadeOut,
		},
	},
	dialogPopup: {
		display: 'flex',
		flexDirection: 'column',
		position: 'fixed',
		outline: 0,
		zIndex: 50,
		border: `1px solid ${borderColor}`,
		backgroundColor: white,
		padding: 24,
		boxShadow: shadowLg,
		[`@media (min-width: ${THEME.breakpoints.sm}px)`]: {
			borderRadius: 8,
		},
		animationDuration: '200ms',
		animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
		animationFillMode: 'forwards',
		':is([data-open])': {
			animationName: fadeInZoomIn,
		},
		':is([data-closed])': {
			animationName: fadeOutZoomOut,
		},
	},
	dialogContentDefault: {
		left: '50%',
		top: '50%',
		maxWidth: 512,
		translate: '-50% -50%',
		width: '100%',
	},
	dialogClose: {
		cursor: 'pointer',
		outline: 0,
		position: 'absolute',
		top: 20,
		right: 20,
		borderRadius: 4,
		opacity: 0.6,
		transitionProperty: 'opacity',
		transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
		transitionDuration: '150ms',
		':hover': {
			opacity: 1,
		},
		':focus': {
			outline: '2px solid transparent',
			outlineOffset: 2,
		},
		':disabled': {
			pointerEvents: 'none',
		},
	},
	closeIcon: {
		width: 12,
		height: 12,
	},
	dialogHeader: {
		display: `flex`,
		flexDirection: 'column',
		gap: 12,
		textAlign: 'center',
		marginBottom: 20,
		[`@media (min-width: ${THEME.breakpoints.sm}px)`]: {
			textAlign: 'start',
		},
	},
	dialogFooter: {
		display: 'flex',
		flexDirection: 'column-reverse',
		paddingTop: 20,
		[`@media (min-width: ${THEME.breakpoints.sm}px)`]: {
			flexDirection: 'row',
			justifyContent: 'flex-end',
			gap: 12,
		},
	},
	dialogTitle: {
		margin: 0,
		fontWeight: 'bold',
		fontSize: 18,
		lineHeight: 1.55,
		leading: 'none',
		tracking: 'tight',
		color: THEME.colors.primary.main,
	},
	dialogBody: {
		flexGrow: 1,
	},
	dialogDescription: {
		margin: 0,
		fontSize: 14,
		lineHeight: 1.43,
	},
	srOnly: {
		position: 'absolute',
		width: 1,
		height: 1,
		padding: 0,
		margin: -1,
		overflow: 'hidden',
		clip: 'rect(0, 0, 0, 0)',
		whiteSpace: 'nowrap',
		borderWidth: 0,
	},
});
